import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SendIcon } from 'lucide-react';
export function Newsletter() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonPosition, setButtonPosition] = useState({
    x: 0,
    y: 0
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    // Get button position for animation start point
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      });
    }
    setIsSubmitting(true);
    setTimeout(() => {
      setSubmitted(true);
      setEmail('');
      setTimeout(() => {
        setIsSubmitting(false);
        setSubmitted(false);
      }, 4000);
    }, 4000);
  };
  return <section className="w-full py-32 px-8 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden" data-id="element-273">
      <div className="max-w-4xl mx-auto relative z-10" data-id="element-274">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-275">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-id="element-276">
            Stay Updated
          </h2>
          <p className="text-xl text-slate-600" data-id="element-277">
            Get insights on quantitative finance and alternative markets
          </p>
        </motion.div>
        <motion.form onSubmit={handleSubmit} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} viewport={{
        once: true
      }} className="max-w-md mx-auto" data-id="element-278">
          <div className="flex gap-3" data-id="element-279">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your.email@example.com" className="flex-1 px-6 py-4 bg-white border-2 border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-600 transition-colors" disabled={isSubmitting} required data-id="element-280" />
            <motion.button ref={buttonRef} type="submit" disabled={isSubmitting || !email} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="px-8 py-4 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-281">
              <SendIcon className="w-5 h-5" data-id="element-282" />
              Send
            </motion.button>
          </div>
        </motion.form>
        {submitted && <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mt-6 text-green-600 font-semibold" data-id="element-283">
            Thanks for subscribing! ✓
          </motion.p>}
      </div>
      {/* Paper Airplane Animation - flies from button to top left */}
      <AnimatePresence data-id="element-284">
        {isSubmitting && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 pointer-events-none z-50" data-id="element-285">
            {/* Main airplane */}
            <motion.div initial={{
          left: buttonPosition.x,
          top: buttonPosition.y,
          opacity: 0,
          rotate: 0,
          scale: 1
        }} animate={{
          left: -100,
          top: -100,
          opacity: [0, 1, 1, 1, 0.8, 0],
          rotate: -45,
          scale: [1, 1.2, 1.5, 1.8, 2, 1.5]
        }} transition={{
          duration: 4,
          ease: [0.22, 1, 0.36, 1]
        }} className="absolute" style={{
          transform: 'translate(-50%, -50%)'
        }} data-id="element-286">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-2xl" data-id="element-287">
                <defs data-id="element-288">
                  <linearGradient id="planeGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-289">
                    <stop offset="0%" stopColor="#3B82F6" data-id="element-290" />
                    <stop offset="100%" stopColor="#1D4ED8" data-id="element-291" />
                  </linearGradient>
                </defs>
                <path d="M22 2L11 13" stroke="url(#planeGradient)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-292" />
                <path d="M22 2L15 22L11 13L2 9L22 2Z" fill="url(#planeGradient)" stroke="url(#planeGradient)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-293" />
              </svg>
            </motion.div>
            {/* Glowing trail following the airplane */}
            {[0, 1, 2, 3, 4, 5, 6, 7].map(index => <motion.div key={`trail-${index}`} className="absolute rounded-full bg-blue-400" style={{
          width: '12px',
          height: '12px',
          boxShadow: '0 0 20px rgba(59, 130, 246, 0.8)'
        }} initial={{
          left: buttonPosition.x,
          top: buttonPosition.y,
          opacity: 0,
          scale: 0
        }} animate={{
          left: -100,
          top: -100,
          opacity: [0, 0.8, 0.6, 0.4, 0],
          scale: [0, 1.5, 2, 2.5, 0]
        }} transition={{
          duration: 4,
          delay: index * 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} style={{
          transform: 'translate(-50%, -50%)'
        }} data-id="element-294" />)}
            {/* Speed lines for extra effect */}
            {[0, 1, 2, 3].map(index => <motion.div key={`line-${index}`} className="absolute bg-gradient-to-r from-blue-500 via-blue-400 to-transparent" style={{
          width: '60px',
          height: '3px',
          transformOrigin: 'left center'
        }} initial={{
          left: buttonPosition.x,
          top: buttonPosition.y,
          opacity: 0,
          rotate: -45
        }} animate={{
          left: -100,
          top: -100,
          opacity: [0, 0.9, 0.7, 0],
          scaleX: [0.5, 1, 1.2, 0.8]
        }} transition={{
          duration: 4,
          delay: index * 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} style={{
          transform: 'translate(-50%, -50%)'
        }} data-id="element-295" />)}
            {/* Sparkle effects */}
            {Array.from({
          length: 15
        }).map((_, index) => {
          const angle = index / 15 * Math.PI * 2;
          const distance = 100 + Math.random() * 50;
          return <motion.div key={`sparkle-${index}`} className="absolute w-2 h-2 bg-white rounded-full" style={{
            boxShadow: '0 0 10px rgba(255, 255, 255, 0.8)'
          }} initial={{
            left: buttonPosition.x,
            top: buttonPosition.y,
            opacity: 0,
            scale: 0
          }} animate={{
            left: buttonPosition.x + Math.cos(angle) * distance,
            top: buttonPosition.y + Math.sin(angle) * distance,
            opacity: [0, 1, 0],
            scale: [0, 1.5, 0]
          }} transition={{
            duration: 1.5,
            delay: Math.random() * 0.5,
            ease: 'easeOut'
          }} style={{
            transform: 'translate(-50%, -50%)'
          }} data-id="element-296" />;
        })}
          </motion.div>}
      </AnimatePresence>
    </section>;
}